
import React, { useState } from 'react';
import StarIcon from './icons/StarIcon';

interface StarRatingProps {
    rating: number; // 0 to 5
    count?: number; // Number of ratings (for display only)
    onRate?: (rating: number) => void;
    readonly?: boolean;
    size?: 'sm' | 'md' | 'lg';
    showCount?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ 
    rating, 
    count, 
    onRate, 
    readonly = false, 
    size = 'md',
    showCount = false
}) => {
    const [hoverRating, setHoverRating] = useState(0);

    const sizeClasses = {
        sm: 'w-3 h-3',
        md: 'w-5 h-5',
        lg: 'w-8 h-8'
    };

    const handleMouseEnter = (index: number) => {
        if (!readonly) setHoverRating(index);
    };

    const handleMouseLeave = () => {
        if (!readonly) setHoverRating(0);
    };

    const handleClick = (index: number) => {
        if (!readonly && onRate) {
            onRate(index);
        }
    };

    const renderStars = () => {
        const stars = [];
        const currentRating = hoverRating || rating;

        for (let i = 1; i <= 5; i++) {
            let isFilled = false;
            let isHalf = false;

            if (hoverRating > 0) {
                // Hover state logic (integers only)
                isFilled = i <= hoverRating;
            } else {
                // Display state logic (supports decimals)
                if (rating >= i) {
                    isFilled = true;
                } else if (rating >= i - 0.5) {
                    isHalf = true;
                }
            }

            stars.push(
                <button
                    key={i}
                    type="button"
                    disabled={readonly}
                    className={`focus:outline-none transition-transform ${!readonly ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}
                    onMouseEnter={() => handleMouseEnter(i)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleClick(i)}
                >
                    <StarIcon 
                        className={`${sizeClasses[size]} ${isFilled || isHalf ? 'text-amber-400' : 'text-slate-300'}`} 
                        filled={isFilled} 
                        half={isHalf}
                    />
                </button>
            );
        }
        return stars;
    };

    return (
        <div className="flex items-center gap-1">
            <div className="flex">
                {renderStars()}
            </div>
            {showCount && (
                <span className="text-xs text-slate-500 font-medium ml-1">
                    {rating > 0 ? rating.toFixed(1) : ''} 
                    {count !== undefined && count > 0 && <span className="text-slate-400 font-normal"> ({count})</span>}
                </span>
            )}
        </div>
    );
};

export default StarRating;
